import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Menu, X } from "lucide-react";
import LangSwitcher from "@/components/LangSwitcher";
import { useLang } from "@/i18n/LangContext";

/**
 * Premium editorial header adapted for the light, luminous palette.
 * theme: "tarq" (navy/sand) | "bbq" (warm cream/wood)
 */
export const BrandHeader = ({ theme = "tarq", logoSrc, name }) => {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();
  const isBbq = theme === "bbq";
  const { t } = useLang();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [menuOpen]);

  const base = "fixed top-0 inset-x-0 z-40 transition-all duration-500 backdrop-blur-xl";
  const chrome = isBbq
    ? scrolled ? "bg-[#1A0E08]/92 border-b border-[#E85A1F]/20" : "bg-[#1A0E08]/35 border-b border-transparent"
    : scrolled ? "bg-[#FBF8F3]/92 border-b border-[#0F2A44]/10" : "bg-[#FBF8F3]/55 border-b border-transparent";

  const ink = isBbq ? "text-[#F5EDD9]" : "text-[#0F2A44]";
  const muted = isBbq ? "text-[#9A8F7D] hover:text-[#F5EDD9]" : "text-[#7A6A54] hover:text-[#0F2A44]";
  const activePill = isBbq ? "bg-[#E85A1F] text-white" : "bg-[#0F2A44] text-white";
  const inactivePill = isBbq ? "text-[#9A8F7D] hover:text-[#F5EDD9]" : "text-[#7A6A54] hover:text-[#0F2A44]";
  const logoFrame = isBbq
    ? "bg-transparent"
    : "bg-white p-1 rounded-sm";

  const navItems = isBbq
    ? [
        { href: "#grelha", label: t("nav.grelha") },
        { href: "#espaco", label: t("nav.experiencia") },
        { href: "#pratos-destaque", label: t("nav.pratos") },
        { href: "#menu", label: t("nav.menu") },
        { href: "#visitar", label: t("nav.visitar") },
      ]
    : [
        { href: "#experiencia", label: t("nav.experiencia") },
        { href: "#brunch", label: "Brunch" },
        { href: "#momentos", label: t("nav.momentos") },
        { href: "#galeria", label: t("nav.galeria") },
        { href: "#menu", label: t("nav.menu") },
        { href: "#visitar", label: t("nav.visitar") },
      ];

  return (
    <>
      <header className={`${base} ${chrome}`} data-testid={`header-${theme}`}>
        <div className="max-w-[1700px] mx-auto px-5 sm:px-8 lg:px-10 h-16 sm:h-[76px] flex items-center justify-between gap-4">
          <button
            data-testid="header-back-gateway"
            onClick={() => navigate("/")}
            aria-label="Voltar à entrada"
            className={`group inline-flex items-center gap-2 ${muted} transition-colors`}
          >
            <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-0.5" />
            <span className={`hidden sm:inline text-[10px] tracking-[0.38em] uppercase ${isBbq ? "font-sans-bbq" : "font-sans-tarq"}`}>
              Entrada
            </span>
          </button>

          <Link
            to={isBbq ? "/bbq" : "/tarquinio"}
            className={`flex items-center gap-3 min-w-0 ${ink}`}
            data-testid={`header-logo-${theme}`}
          >
            {isBbq ? (
              <span
                className="inline-flex flex-col leading-none gap-[3px] shrink-0"
                aria-label={name}
              >
                <span className="font-serif-bbq text-[#F5EDD9] text-[1.35rem] sm:text-[1.5rem] tracking-tight leading-none">
                  BBQ
                </span>
                <span className="font-sans-bbq text-[#E85A1F] text-[8px] sm:text-[9px] tracking-[0.42em] uppercase leading-none">
                  Assador
                </span>
              </span>
            ) : (
              <div className={logoFrame}>
                <img src={logoSrc} alt={name} className="h-8 sm:h-9 w-auto object-contain" />
              </div>
            )}
            {!isBbq && (
              <span className="hidden md:inline truncate font-serif-tarq italic tracking-wide text-base lg:text-lg">
                {name}
              </span>
            )}
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {navItems.map((n) => (
              <a
                key={n.href}
                href={n.href}
                className={`link-underline text-[10px] tracking-[0.32em] uppercase ${muted} transition-colors ${isBbq ? "font-sans-bbq" : "font-sans-tarq"}`}
                data-testid={`nav-${n.href.replace("#", "")}`}
              >
                {n.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <LangSwitcher
              variant={isBbq ? "dark" : "light"}
              className="hidden sm:block"
            />
            <div className={`flex items-center p-1 rounded-full border ${isBbq ? "border-[#F5EDD9]/20" : "border-[#0F2A44]/15"}`} data-testid="brand-switch">
              <Link
                to="/tarquinio"
                className={`px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-[11px] tracking-[0.28em] uppercase transition-all ${!isBbq ? activePill : inactivePill} ${isBbq ? "font-sans-bbq" : "font-sans-tarq"}`}
                data-testid="switch-tarquinio"
              >
                Tarquínio
              </Link>
              <Link
                to="/bbq"
                className={`px-3 sm:px-4 py-1.5 rounded-full text-[10px] sm:text-[11px] tracking-[0.28em] uppercase transition-all ${isBbq ? activePill : inactivePill} ${isBbq ? "font-sans-bbq" : "font-sans-tarq"}`}
                data-testid="switch-bbq"
              >
                BBQ
              </Link>
            </div>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Abrir menu"
              className={`lg:hidden ml-1 p-2 ${ink}`}
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </header>

      {menuOpen && (
        <div
          className={`lg:hidden fixed inset-0 z-[45] pt-[76px] ${isBbq ? "bg-[#1A0E08]" : "bg-[#FBF8F3]"}`}
          data-testid="mobile-menu"
        >
          <div className="px-6 py-10 flex flex-col gap-1">
            {navItems.map((n, i) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setMenuOpen(false)}
                style={{ animationDelay: `${i * 60}ms` }}
                className={`reveal-up is-visible block py-4 border-b ${isBbq ? "border-[#F5EDD9]/10 text-[#F5EDD9] font-serif-bbq" : "border-[#0F2A44]/10 text-[#0F2A44] font-serif-tarq italic"} text-3xl tracking-tight`}
              >
                {n.label}
              </a>
            ))}
            <div className="mt-8 flex items-center justify-between">
              <span
                className={`text-[10px] tracking-[0.4em] uppercase ${
                  isBbq ? "text-[#F5EDD9]/60" : "text-[#7A6A54]"
                } ${isBbq ? "font-sans-bbq" : "font-sans-tarq"}`}
              >
                {t("lang.switch")}
              </span>
              <LangSwitcher variant={isBbq ? "dark" : "light"} />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BrandHeader;
